import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Trophy, 
  BarChart3, 
  Coins, 
  Gamepad2, 
  User,
  TrendingUp,
  Activity,
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react';
import RealtimeNotifications from './RealtimeNotifications';

interface DashboardStats {
  totalUsers: number;
  activeGames: number;
  totalGames: number;
  waitingGames: number;
  playingGames: number;
  completedGames: number;
  totalCoins: number;
  systemHealth: 'good' | 'warning' | 'error';
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeGames: 0,
    totalGames: 0,
    waitingGames: 0,
    playingGames: 0,
    completedGames: 0,
    totalCoins: 0,
    systemHealth: 'good'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Itt lehetne valós adatokat betölteni, most mock
    setStats({
      totalUsers: 42,
      activeGames: 8,
      totalGames: 156,
      waitingGames: 3,
      playingGames: 5,
      completedGames: 148,
      totalCoins: 12500,
      systemHealth: 'good'
    });
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const getSystemStatusIcon = (status: string) => {
    switch (status) {
      case 'good':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Activity className="h-5 w-5 text-gray-500" />;
    }
  };

  const getSystemStatusText = (status: string) => {
    switch (status) {
      case 'good':
        return 'Jó';
      case 'warning':
        return 'Figyelmeztetés';
      case 'error':
        return 'Hiba';
      default:
        return 'Ismeretlen';
    }
  };

  const getSystemStatusColor = (status: string) => {
    switch (status) {
      case 'good':
        return 'text-green-600 dark:text-green-400';
      case 'warning':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'error':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Áttekintés</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            A Game Winners játék adminisztrációs panelje
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button className="btn-primary">
            <TrendingUp className="mr-2 h-4 w-4" />
            Részletes jelentés
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card animate-bounce-in" style={{ animationDelay: '0.1s' }}>
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Összes felhasználó</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalUsers}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card animate-bounce-in" style={{ animationDelay: '0.2s' }}>
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                  <Gamepad2 className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Aktív játékok</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.activeGames}</p>
                <p className="text-xs text-green-600 dark:text-green-400">
                  {stats.waitingGames} várakozó, {stats.playingGames} játszik
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card animate-bounce-in" style={{ animationDelay: '0.3s' }}>
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center">
                  <Trophy className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Befejezett játékok</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.completedGames}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card animate-bounce-in" style={{ animationDelay: '0.4s' }}>
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                  <Coins className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Összes aranytallér</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalCoins.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Játék Statisztikák</h3>
          </div>
          <div className="card-body">
            <dl className="grid grid-cols-2 gap-6">
              <div className="text-center">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Várakozó játékok</dt>
                <dd className="mt-2 text-3xl font-bold text-yellow-600 dark:text-yellow-400">{stats.waitingGames}</dd>
              </div>
              <div className="text-center">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Aktív játékok</dt>
                <dd className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">{stats.playingGames}</dd>
              </div>
              <div className="text-center">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Befejezett játékok</dt>
                <dd className="mt-2 text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.completedGames}</dd>
              </div>
              <div className="text-center">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Összes játék</dt>
                <dd className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{stats.totalGames}</dd>
              </div>
            </dl>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Rendszer Állapot</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Adatbázis kapcsolat</span>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-green-600 dark:text-green-400 text-sm">Aktív</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Játék szerver</span>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-green-600 dark:text-green-400 text-sm">Fut</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Felhasználó autentikáció</span>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-green-600 dark:text-green-400 text-sm">Működik</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Valós idejű szinkronizáció</span>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-green-600 dark:text-green-400 text-sm">Aktív</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Rendszer állapot</span>
                <div className="flex items-center space-x-2">
                  {getSystemStatusIcon(stats.systemHealth)}
                  <span className={`text-sm ${getSystemStatusColor(stats.systemHealth)}`}>
                    {getSystemStatusText(stats.systemHealth)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Combined Layout: Recent Activity and Real-time Notifications */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Activity */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Legutóbbi tevékenységek</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Új felhasználó regisztrált
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">2 perce</p>
                </div>
                <Clock className="h-4 w-4 text-gray-400" />
              </div>

              <div className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center">
                    <Trophy className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Játék befejezve
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">5 perce</p>
                </div>
                <Clock className="h-4 w-4 text-gray-400" />
              </div>

              <div className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                    <Coins className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Aranytallér vásárlás
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">10 perce</p>
                </div>
                <Clock className="h-4 w-4 text-gray-400" />
              </div>

              <div className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                    <Gamepad2 className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Új játék indult
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">15 perce</p>
                </div>
                <Clock className="h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Real-time Notifications */}
        <RealtimeNotifications />
      </div>
    </div>
  );
};

export default Dashboard; 